<template>
  <BaseDialog max-width="900" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="$t('nexus.modal.title')" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('nexus.modal.action_label') + '*'"
            />
            <BaseTextField
              v-model="form.nexusFolderCreateButtonLabel"
              :placeholder="$t('nexus.modal.action_label') + '*'"
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('nexus.modal.folder_label') + '*'"
            />
            <BaseTextField
              v-model="form.nexusFolderNameInputLabel"
              :placeholder="$t('nexus.modal.folder_label') + '*'"
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('nexus.modal.launchElementAfterCollection') + '*'"
            />
            <base-autocomplete
              v-model="form.nexusFolderFormTpl"
              :loading="formTemplates.loading"
              :items="formTemplates.items"
              item-text="title"
              item-value="id"
              :has-error="!!nexusFolderFormTplError"
              :error-messages="nexusFolderFormTplError"
              :disabled="isSubmiting"
              solo
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('utils.assignedCollaborator')"
            />
            <base-autocomplete
              v-model="form.nexusFolderAssignedCollaborator"
              :loading="collaboratorFilter.loading"
              :items="collaboratorFilter.items"
              :filter="() => true"
              :search-input.sync="collaboratorFilter.email"
              item-text="user.email"
              item-value="user.id"
              solo
              :placeholder="`${$tc('organization.collaborator', 2)} `"
            >
              <template #append-item>
                <div v-intersect="loadMoreCollaborators" />
              </template>
            </base-autocomplete>
          </div>
          <label
            for="isPublic"
            class="label-black"
            v-text="$t('nexus.modal.restrain_creation_to_contact')"
          />
          <div class="contacts_container mb-5">
            <div class="fileds_container">
              <!-- Contact available -->
              <div class="section">
                <div
                  class="section_title"
                  v-text="$t('utils.available_contact')"
                />
                <div class="section_header">
                  <v-text-field
                    v-model="contactFilter.items.queryString"
                    class="search_field"
                    outlined
                    :placeholder="`${$t('utils.search')}...`"
                    prepend-inner-icon="$mdiMagnify"
                    dense
                    hide-details
                    @input="filterContactClient()"
                  />
                </div>
                <div class="section_body">
                  <button
                    v-for="(client, i) in availableClients"
                    :key="`q-${i}`"
                    class="question_item"
                    @click.stop="selectClient(client)"
                  >
                    <div class="info_section">
                      <div class="question_label" v-text="client.email" />
                    </div>
                    <div class="action">
                      <BaseButtonIcon
                        color="primary"
                        icon="$mdiArrowRight"
                        @click.stop="selectClient(client)"
                      />
                    </div>
                  </button>
                  <template v-if="contactFilter.items.loading">
                    <div
                      v-for="value in 5"
                      :key="value"
                      class="folder_item_skeleton"
                    >
                      <v-skeleton-loader class="skeleton" type="text" />
                    </div>
                  </template>
                  <div v-else v-intersect="loadMoreClients" />
                </div>
              </div>
              <!-- Contact selected -->
              <div class="section">
                <div
                  class="section_title"
                  v-text="$t('utils.selected_contact')"
                />
                <div class="section_header">
                  <v-text-field
                    v-model="search_str"
                    class="search_field"
                    outlined
                    :placeholder="`${$t('utils.search')}...`"
                    prepend-inner-icon="$mdiMagnify"
                    dense
                    hide-details
                  />
                </div>
                <div class="section_body">
                  <button
                    v-for="(client, i) in clientsSelect"
                    :key="`ql-${i}`"
                    class="question_item"
                    @click.stop="unselectClient(client)"
                  >
                    <div class="info_section">
                      <div class="question_label" v-text="client.email" />
                    </div>
                    <div class="action">
                      <BaseButtonIcon
                        color="secondary"
                        icon="$mdiClose"
                        @click.stop="unselectClient(client)"
                      />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="checkbox_item">
            <input
              id="nexusFolderContactSelectionAllowed"
              v-model="form.nexusFolderContactSelectionAllowed"
              type="checkbox"
              name="nexusFolderContactSelectionAllowed"
              @change="resetContactCreation()"
            />
            <label
              for="nexusFolderContactSelectionAllowed"
              v-text="$t('nexus.modal.enableContactSelectionForFolders')"
            />
          </div>
          <div class="checkbox_item">
            <input
              id="nexusContactCreationForFolders"
              v-model="form.nexusFolderContactCreationAllowed"
              type="checkbox"
              name="nexusContactCreationForFolders"
              :disabled="!form.nexusFolderContactSelectionAllowed"
            />
            <label
              for="nexusContactCreationForFolders"
              v-text="$t('nexus.modal.enableContactCreationForFolders')"
            />
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :loading="isSubmiting"
            color="primary"
            type="primary"
            @click="submit()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>
<script>
import OrganizationAPI from "@/core/Services/organization.api";
import { getOrganizationCampaigns } from "@/modules/Editor/Services/campaign.service";

import {
  getOrganizationWorkflowCosmoformTemplate,
  patchOrganizationWorkflow,
} from "@/modules/Admin/Services/workflow.service";
import { validationMixin } from "vuelidate";
import { requiredIf } from "vuelidate/lib/validators";
import { getOrganizationContacts } from "@/core/Services/organization.service";
export default {
  name: "WorkflowNexusConfigurationDialog",
  components: {},
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    workflow: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSubmiting: false,
      search_str: "",
      form: {
        nexusFolderCreationEnabled: true,
        nexusFolderContactCreationAllowed: false,
        nexusFolderContactSelectionAllowed: false,
        nexusFolderCreationRestrictedTo: [],
        nexusFolderCreateButtonLabel: "",
        nexusFolderNameInputLabel: "",
        nexusFolderAssignedCollaborator: "",
        nexusFolderFormTpl: null,
      },
      collectionType: [
        { text: this.$t("utils.campaign"), value: "CAMPAIGN" },
        { text: this.$tc("workflow_detail.tabs.form", 1), value: "FORM" },
      ],
      collaboratorFilter: {
        loading: false,
        page: 1,
        items: [],
        pageSize: 10,
        email: undefined,
        searchTimeoutRef: undefined,
      },
      contactFilter: {
        items: {
          loading: false,
          page: 1,
          pageSize: 100,
          results: [],
          searchTimeoutRef: undefined,
          next: null,
          queryString: "",
        },
      },
      campaign: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      formTemplates: {
        items: [],
        loading: false,
      },
      campaignFilter: {
        loading: false,
        page: 1,
        pageSize: 100,
        name: undefined,
        status: "IN_PROGRESS",
        searchTimeoutRef: undefined,
      },
    };
  },
  validations() {
    let validations = {};
    validations = {
      form: {
        nexusFolderFormTpl: {
          required: requiredIf(() => this.form.nexusFolderCreationEnabled),
        },
      },
    };
    return validations;
  },

  computed: {
    nexusFolderFormTplError() {
      if (!this.$v.form.nexusFolderFormTpl.$dirty) return "";
      if (!this.$v.form.nexusFolderFormTpl.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    availableClients() {
      if (!this.form.nexusFolderCreationRestrictedTo.length)
        return this.contactFilter.items.results;
      return this.contactFilter.items.results.filter(
        (client) =>
          !this.form.nexusFolderCreationRestrictedTo.some(
            (selected) => selected.id === client.id
          )
      );
    },
    clientsSelect() {
      if (!this.search_str) return this.form.nexusFolderCreationRestrictedTo;
      return this.form.nexusFolderCreationRestrictedTo.filter((client) =>
        client.email.toLowerCase().includes(this.search_str.toLowerCase())
      );
    },
  },
  watch: {
    "collaboratorFilter.email"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCollaborators();
    },
    "campaignFilter.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCampaigns();
    },
  },
  created() {
    this.initializeForm();
    this.fetchCollaborators();
    this.fetchClients();
    this.initFormCourse();
  },
  methods: {
    resetForm(value) {
      if (!value) {
        this.form.nexusFolderContactCreationAllowed = false;
        this.form.nexusFolderContactSelectionAllowed = false;
      }
    },
    selectClient(client) {
      this.form.nexusFolderCreationRestrictedTo.push(client);
    },
    unselectClient(client) {
      this.form.nexusFolderCreationRestrictedTo =
        this.form.nexusFolderCreationRestrictedTo.filter(
          (cl) => cl.id !== client.id
        );
    },
    initFormCourse() {
      this.fetchCosmoformTemplates();
    },

    //contact
    async fetchClients() {
      if (
        this.contactFilter.items.loading &&
        !this.contactFilter.items.page &&
        !this.organizationId
      )
        return;
      this.contactFilter.items.loading = true;
      const res = await getOrganizationContacts(this.organizationId);
      this.contactFilter.items = {
        ...this.contactFilter.items,
        results: [...this.contactFilter.items.results, ...res.data.results],
        loading: false,
        next: res.data.next,
      };
    },
    async loadMoreClients(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.contactFilter.items.loading ||
        !this.contactFilter.items.next
      )
        return;
      try {
        this.contactFilter.items.page = this.contactFilter.items.next;
        await this.fetchClients();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    filterContactClient() {
      this.contactFilter.items.results = [];
      this.contactFilter.items.page = 1;
      if (this.contactFilter.items.searchTimeoutRef)
        clearTimeout(this.contactFilter.items.searchTimeoutRef);
      this.contactFilter.items.searchTimeoutRef = setTimeout(async () => {
        this.fetchClients();
      }, 500);
    },
    async fetchEventCampaigns() {
      this.campaignFilter.loading = true;
      try {
        this.campaignFilter.page = 1;
        const res = await getOrganizationCampaigns({
          organizationId: this.organizationId,
          filters: this.campaignFilter,
        });
        this.campaign = {
          ...res.data,
          items: [...res.data.results],
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.campaignFilter.loading = false;
    },
    filterCampaigns() {
      if (this.campaignFilter.searchTimeoutRef)
        clearTimeout(this.campaignFilter.searchTimeoutRef);
      this.campaignFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchEventCampaigns();
      }, 500);
    },
    async fetchCosmoformTemplates() {
      try {
        this.formTemplates.loading = true;
        const response = await getOrganizationWorkflowCosmoformTemplate({
          organizationId: this.organizationId,
          workflowId: this.$route.params.workflowId,
          filters: {},
        });
        this.formTemplates.items = response.item;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.formTemplates.loading = false;
    },
    reset() {
      if (this.isSubmiting) return;
      this.$emit("close");
    },
    initializeForm() {
      this.form.nexusFolderFormTpl = this.workflow.nexusFolderFormTpl?.id;
      this.form.nexusFolderContactCreationAllowed =
        this.workflow.nexusFolderContactCreationAllowed;
      this.form.nexusFolderContactSelectionAllowed =
        this.workflow.nexusFolderContactSelectionAllowed;
      this.form.nexusFolderCreationRestrictedTo =
        this.workflow.nexusFolderCreationRestrictedTo.map((contact) => contact);
      this.form.nexusFolderDataCollection =
        this.workflow.nexusFolderDataCollection;
      this.form.nexusFolderDataCollectionContentId =
        this.workflow.nexusFolderDataCollectionContentId;
      this.form.nexusFolderCreateButtonLabel =
        this.workflow.nexusFolderCreateButtonLabel;
      this.form.nexusFolderNameInputLabel =
        this.workflow.nexusFolderNameInputLabel;
      if (this.workflow.nexusFolderAssignedCollaborator) {
        this.form.nexusFolderAssignedCollaborator =
          this.workflow.nexusFolderAssignedCollaborator.id;
        this.collaboratorFilter.email =
          this.workflow.nexusFolderAssignedCollaborator.email;
      }
    },
    async fetchCollaborators() {
      if (
        this.collaboratorFilter.loading &&
        !this.collaboratorFilter.page &&
        !this.organizationId
      )
        return;
      this.collaboratorFilter.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaboratorFilter.page,
        this.collaboratorFilter.email
      );
      this.collaboratorFilter = {
        ...this.collaboratorFilter,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.collaboratorFilter.loading = false;
    },
    filterCollaborators() {
      this.collaboratorFilter.page = 1;
      if (this.collaboratorFilter.searchTimeoutRef)
        clearTimeout(this.collaboratorFilter.searchTimeoutRef);
      this.collaboratorFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 200);
    },
    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaboratorFilter.loading ||
        !this.collaboratorFilter.next
      )
        return;
      this.collaboratorFilter.page = this.collaboratorFilter.next;
      this.collaboratorFilter.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationCollaborators(
          this.organizationId,
          this.collaboratorFilter.page,
          this.collaboratorFilter.email
        );
        this.collaboratorFilter = {
          items: [...this.collaboratorFilter.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.isSubmiting = true;
      try {
        const res = await patchOrganizationWorkflow(
          this.organizationId,
          this.workflow.id,
          {
            nexus_folder_creation_enabled: this.form.nexusFolderCreationEnabled,
            nexus_folder_form_tpl: this.form.nexusFolderFormTpl,
            nexus_folder_contact_selection_allowed:
              this.form.nexusFolderContactSelectionAllowed,
            nexus_folder_creation_restricted_to:
              this.form.nexusFolderCreationRestrictedTo.map(
                (client) => client.id
              ),

            nexus_folder_contact_creation_allowed:
              this.form.nexusFolderContactCreationAllowed,
            nexus_folder_name_input_label: this.form.nexusFolderNameInputLabel
              ? this.form.nexusFolderNameInputLabel
              : "Nom",
            nexus_folder_create_button_label: this.form
              .nexusFolderCreateButtonLabel
              ? this.form.nexusFolderCreateButtonLabel
              : this.workflow.name,
            nexus_folder_assigned_collaborator:
              this.form.nexusFolderAssignedCollaborator,
          }
        );
        this.$emit("workflowUpdated", {
          ...this.workflow,
          ...res.data,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.snackbar.success.workflowModifiedSuccessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.error.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
    resetContactCreation() {
      if (this.form.nexusFolderContactSelectionAllowed) return;
      this.form.nexusFolderContactCreationAllowed = false;
      this.form.nexusFolderCreationRestrictedTo = [];
    },
  },
};
</script>
<style lang="scss">
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  .checkbox_item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    label {
      color: #353542;
      font-family: "Inter";
      font-size: 14px;
      line-height: 18px;
      font-style: normal;
      font-weight: 400;
    }
    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
  .form_group {
    .input_label {
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 6px;
      color: #353542;
    }
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  gap: 16px;
}
.fileds_container {
  width: 100%;
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  .section {
    flex: none;
    width: calc(50% - 10px);
    .section_title {
      font-size: 16px;
      line-height: 20px;
      margin-bottom: 10px;
      color: #353542;
    }
    .section_header {
      padding: 8px;
      background: #ecebf1;
      .search_field {
        background: #fff;
      }
    }
    .section_body {
      position: relative;
      border: 0.5px solid #ecebf1;
      padding: 12px;
      height: 312px;
      overflow: hidden;
      overflow-y: auto;
      &.error_section {
        border-color: red;
      }
      .question_item {
        padding: 6px 10px;
        align-items: center;
        display: flex;
        width: 100%;
        gap: 4px;
        border-radius: 5px;
        margin-bottom: 12px;
        cursor: pointer;
        text-align: start;
        background: #fff;
        transition: all 0.25s ease-in-out;
        &:hover {
          background: #f4f7ff;
        }
        .info_section {
          width: calc(100% - 40px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          .question_label {
            color: #2536cc;
            font-size: 12px;
            font-weight: 600;
            line-height: 20px;
          }
          .question_slug {
            color: #707080;
            font-size: 12px;
            font-weight: 400;
            line-height: 16px;
            text-transform: uppercase;
          }
        }
        .action {
          width: 36px;
          height: 36px;
          flex: none;
        }
      }
      .error_message {
        text-align: center;
        color: red;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .folder_item_skeleton {
        width: 100%;
        height: 44px;
        margin-bottom: 12px;
        .skeleton {
          height: 100%;
          width: 100%;
          ::v-deep {
            .v-skeleton-loader__bone {
              height: 100%;
            }
          }
        }
      }
    }
  }
}
.contacts_container {
  width: 100%;
  padding: 18px;
  border: 1px solid gray;
  border-radius: 12px;
}
.label-black {
  color: #353542;
  font-family: "Inter";
  font-size: 14px;
  line-height: 18px;
  font-style: normal;
  font-weight: 400;
}
</style>
